/*
  ════════════════════════════════════════════════════════════════════════════
  THE SAFEPASS MARK

  The symbol is "the pass": an issued credential with a detachable stub. The
  body carries the code field (three cells), the stub is the portion presented
  and validated at the gate, and the perforation divides them. That is
  literally what the product does once a day, per child.

  Inline SVG rather than an <img>, for two reasons:

  1. The mark has to sit on the navy command bar at 24px AND on a white card
     at 32px, in both themes. As an image that would be four files kept in
     sync by hand; here it is one `tone` prop.
  2. At 16–24px an external file arrives a frame late and the bar visibly
     pops. Inline, it paints with the first frame.

  Geometry is copied exactly from brand/safepass-symbol.svg — the 128 grid,
  the 4px radius at product scale, the 7px hairline. Do not nudge it here; if
  the mark changes, it changes in brand/source/generate.py and comes back out
  through that file. The navy/blue are the identity's own inks, NOT theme
  tokens: the mark does not restyle itself per theme, it picks a cut.

  The shield glyph this replaces was a placeholder the brief explicitly
  excludes (docs/BRAND-BRIEF.md §6) — a plain shield says "security software",
  not "your child got home".
  ════════════════════════════════════════════════════════════════════════════
*/

import { useId } from 'react';

/** Which cut of the mark to paint. */
export type LogoTone =
  /** Navy body, blue stub. For white and light surfaces. */
  | 'default'
  /** White body, lighter blue stub. For the navy command bar and dark cards. */
  | 'reversed'
  /** Single-colour, inherits `currentColor`. For dense or one-ink contexts. */
  | 'mono';

interface LogoProps {
  size?: number;
  tone?: LogoTone;
  /** Drop the three code cells. Below ~20px they silt up — see the usage sheet. */
  simple?: boolean;
  /*
    The perforation is a knockout, not an ink: it is the surface showing
    through the tear. The two-colour cuts know their own ground, but `mono`
    could be sitting on anything, so its ground has to be named. White is the
    same assumption brand/safepass-symbol-mono-black.svg makes.
  */
  knockout?: string;
  className?: string;
}

const INKS: Record<LogoTone, { body: string; stub: string; cells: string; perf: string | null }> = {
  default:  { body: '#101E32', stub: '#1157D4', cells: '#101E32', perf: '#FFFFFF' },
  reversed: { body: '#FFFFFF', stub: '#4D94F5', cells: '#FFFFFF', perf: '#FFFFFF' },
  mono:     { body: 'currentColor', stub: 'currentColor', cells: 'currentColor', perf: null },
};

export function Logo({
  size = 24,
  tone = 'default',
  simple = false,
  knockout = '#FFFFFF',
  className,
}: LogoProps) {
  const ink = INKS[tone];

  /*
    A clipPath needs a document-unique id. Two marks on one page — the command
    bar and a card — sharing one id makes the second clip against the first's
    rect and the stub fill vanishes, so each instance gets its own.
  */
  const clipId = `sp-mark-${useId()}`;

  /* Below 20px the cells close up into a smudge; the simple cut is the
     brand's own answer to that, not an improvisation. */
  const showCells = !simple && size >= 20;

  return (
    <svg
      viewBox="0 0 128 128"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="SafePass"
      focusable="false"
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="12" y="28" width="104" height="72" rx="4" />
        </clipPath>
      </defs>

      {/* The stub, clipped to the card so its fill stops at the rounded edge */}
      <g clipPath={`url(#${clipId})`}>
        <rect x="82" y="28" width="34" height="72" fill={ink.stub} />
      </g>

      {showCells && (
        <>
          <rect x="25" y="54" width="10" height="20" rx="2" fill={ink.cells} />
          <rect x="41" y="54" width="10" height="20" rx="2" fill={ink.cells} />
          <rect x="57" y="54" width="10" height="20" rx="2" fill={ink.cells} />
        </>
      )}

      <rect
        x="12"
        y="28"
        width="104"
        height="72"
        rx="4"
        fill="none"
        stroke={ink.body}
        strokeWidth="7"
      />

      {/* The perforation, torn through the whole object */}
      <line
        x1="82"
        y1="34"
        x2="82"
        y2="94"
        stroke={ink.perf ?? knockout}
        strokeWidth="5"
        strokeDasharray="7 6"
        strokeLinecap="butt"
      />
    </svg>
  );
}
